import { ColDef,  RowDoubleClickedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { aggridHeaderClass } from "src/components/common/common";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import {  defaultColDef, getDefaultRowIndexColumnConfig } from "src/utils/grid/gridStyleUtils";

import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import Grid_Select from "src/components/common/gridWrapper/Grid_Select";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { PRD2030_R01Dto, defaultProductResultData, GridRowData } from "./Dto_PRD2030_R01";
import { usePRD2030_R01Store } from "./Store_PRD2030_R01";
import { formatDate } from "src/utils/string/stringUtil";

import useUiuxStore from "src/store/useUiuxStore";
import {  CommonCodeColumn, CompCdColumn, WorkPlaceColumn } from "src/utils/grid/GridColumnContent";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { onlyTouchGridStyle } from "src/utils/grid/gridFunctionUtil";
import { cellDateComparator } from 'src/utils/grid/gridFunctionUtil';

const COLUMN_STATE_KEY = 'PRD2030_R01-grid-column-state';

interface Props {
    gridRef: React.RefObject<AgGridReact<PRD2030_R01Dto> | null>;
}

export const Table_PRD2030_R01 = (props: Props) => {

    const { gridRef } = props;

    const { 
        PRD2030_R01List,
        selectedPRD2030_R01List, setSelectedPRD2030_R01List, initSelectedPRD2030_R01List,
        
        columnState, setColumnState,
        scrollTop, setScrollTop,
    } = usePRD2030_R01Store();

    const { 
        jobTeamList,
        ordTypeList,
        unitList,
        compCdList, 
        employeeCdList,
        workPlaceList,
        workProcessList,
    } = useCommonCodeStore();
	const { addTabList, setActiveTab } = useUiuxStore();

    const codeMaps = useMemo(() => {
        return {
            ordType: new Map(ordTypeList.map(v => [v.minorCd, v.codeNm])),
            workPlace: new Map(workPlaceList.map(v => [v.workplaceCd, v.workplaceNm])),
            unit: new Map(unitList.map(v => [v.minorCd, v.codeNm])),
            emp: new Map(employeeCdList.map(v => [v.empNo, v.empNm])),
            comp: new Map(compCdList.map(v => [v.compCd, v.compNm])),
        };
    }, [ordTypeList, workPlaceList, unitList, employeeCdList, compCdList]);

    // 복합키 생성 함수
    // const getCompositeKey = useCallback((row: GridRowData) => 
    //     `${row.transportNo}|${row.itemCd}|${row.customCd}|${row.warehouseCd}`,
    // []);

    const {
        createRowClassRules,
        ensureSelectedRow,
        settingRowIndex,
    } = useGridSettingContent({
        gridRef,
        key: 'valueIndex',
        gridData: PRD2030_R01List || [],
        defaultGridData: defaultProductResultData as GridRowData,
        selectedRow: selectedPRD2030_R01List,
    });
    const getRowIdForGrid = useCallback((params: any) => {
        const id = params.data?.valueIndex || params.id;
        return String(id);
    }, []);
    const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

    const headerNames: Record<string, string> = {
        rowIndex: '',
        
    };

    const getCellStyle = useCallback((params: unknown, key: string) => onlyTouchGridStyle(key, 'productWorkNo')(params), []);

    const baseColumnDefs: ColDef<PRD2030_R01Dto>[] = useMemo(() => [
            ...getDefaultRowIndexColumnConfig(),
			{
				headerName: '공정배정No', colId: 'productWorkNo', field: 'productWorkNo', width: 150,
				editable: false,
				cellStyle: (p) => getCellStyle(p, 'productWorkNo'),
			},
			{
				headerName: '사업장', colId: 'compCd', field: 'compCd', width: 100,
				cellStyle: (p) => getCellStyle(p, 'compCd'),
				...CompCdColumn.options(compCdList, false, undefined, undefined),
			},
			{
				headerName: '작업일자', colId: 'workDt', field: 'workDt', width: 100,
				editable: false,
				cellStyle: (p) => getCellStyle(p, 'workDt'),
				valueFormatter: (p: { data?: PRD2030_R01Dto }) =>
					p.data?.workDt ? formatDate(new Date(p.data.workDt)) : '',
				comparator: cellDateComparator,
			},
			{
				headerName: '작업공정', colId: 'workplaceCd', field: 'workplaceCd', width: 100,
				cellStyle: (p) => getCellStyle(p, 'workplaceCd'),
				...WorkPlaceColumn.options(workPlaceList.filter(opt => opt.useYn === 'Y' && opt.orderYn === 'Y'), false, (params) => params.data?.compCd, undefined, () => {}),
			},
			{
				headerName: '공정유형', colId: 'workProcess', field: 'workProcess', width: 100,
				cellStyle: (p) => getCellStyle(p, 'workProcess'),
				...CommonCodeColumn.options(workProcessList, false, undefined, undefined),
			},
			{
				headerName: '작업팀', colId: 'jobTeam', field: 'jobTeam', width: 100,
				...CommonCodeColumn.options(jobTeamList, false, undefined, undefined),
			},
			{
				headerName: '작업자', colId: 'workEmp', field: 'workEmp', width: 100,
				editable: false,
				cellStyle: (p) => getCellStyle(p, 'workEmp'),
			},
			{
				headerName: '작업자명', colId: 'workEmpNm', field: 'workEmpNm', width: 100,
				editable: false,
				cellStyle: (p) => getCellStyle(p, 'workEmpNm'),
			},
			{
				headerName: '품목코드', colId: 'itemCd', field: 'itemCd', width: 150,
				editable: false,
				cellStyle: (p) => getCellStyle(p, 'itemCd'),
			},
			{
				headerName: '품명', colId: 'itemNm', field: 'itemNm', width: 200,
				editable: false,
				cellStyle: (p) => getCellStyle(p, 'itemNm'),
			},
			{
				headerName: '규격', colId: 'itemSpec', field: 'itemSpec', width: 200,
				editable: false,
				cellStyle: (p) => getCellStyle(p, 'itemSpec'),
			},
			{
				headerName: '지시량', colId: 'orderQty', field: 'orderQty', width: 100,
				editable: false,
				cellStyle: (p) => getCellStyle(p, 'orderQty'),
				valueFormatter: (p: { value?: number | null }) =>
					p.value != null ? p.value.toLocaleString() : '',
			},
    ], [getCellStyle, cellDateComparator, compCdList, workPlaceList, workProcessList, jobTeamList]);

    const defaultColumnState = useMemo(
        () => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
    , [baseColumnDefs]);

    const columnDefs: ColDef<GridRowData>[] = useMemo(() => {
        if (columnState === null) {
            return baseColumnDefs;
        }
        return GridCustomColumnDefs<PRD2030_R01Dto>({
            baseColumnDefs,
            columnState,
            headerNames,
            fallbackHeaderClass: aggridHeaderClass,
        });
    }, [columnState, settingRowIndex, baseColumnDefs, headerNames]);

    const getRowStyleForPUR2020 = useCallback((params: any) => {
        if (params.node.rowPinned === 'bottom') {
            return { background: '#F0F0F0', fontWeight: 'bold', borderTop: 'none' };
        }
        return undefined;
    }, []);

    const shiftRangeSelectionConfig = useMemo(() => ({
        enableShiftRangeSelect: true,
        idKey: 'productWorkNo' as const,
        selectedIds: selectedPRD2030_R01List as Array<string | number>,
        setSelectedIds: (ids: Array<string | number>) => setSelectedPRD2030_R01List(ids.map(String)),
    }), [selectedPRD2030_R01List, setSelectedPRD2030_R01List]);

	
	// 더블클릭으로 KSK2020_R01 페이지로 이동
	const handleRowDoubleClick = useCallback((e: RowDoubleClickedEvent<PRD2030_R01Dto>) => {
		if (e.node.rowPinned === 'bottom') return;
		const rowData = e.data;
		if (!rowData) return;
		
	}, [addTabList, setActiveTab]);

    const pinnedBottomRow = useMemo(() => {
        const detailRows = PRD2030_R01List;
        const totals = detailRows.reduce((acc, item) => {
            return {
                editable: false,
            };
        }, { editable: false });

        return [{
            rowIndex: '',
            productWorkNo: detailRows.length.toLocaleString() + "건",
            editable: false,
        }];
    }, [PRD2030_R01List]);

    useEffect(() => { ensureSelectedRow(); }, [PRD2030_R01List, selectedPRD2030_R01List]);

    // 복합키 기반 rowClassRules
    const rowClassRules = useMemo(() => ({
        'selected': (params: any) => {
            if (!params.data) return false;
            // const compositeKey = params.data.valueIndex;
            return selectedPRD2030_R01List.includes(params.data.productWorkNo);
        }
    }), [selectedPRD2030_R01List]);

    const handleRowDragEnd = useCallback((e: any) => {
        const api = gridRef.current?.api || e?.api;
        if (!api) return;
        
        if (api.applyColumnState) {
            api.applyColumnState({ defaultState: { sort: null as any, sortIndex: null as any } });
        }
        
        const reordered: GridRowData[] = [];
        api.forEachNodeAfterFilterAndSort((node: any, idx: number) => {
            if (!node?.data) return;
            reordered.push({
                ...node.data,
                orderIndex: idx + 1,
                orderIndexSeq: 0,
            } as GridRowData);
        });
        // 드래그 후 리스트 자체는 유지 (필요시 상태 업데이트 로직 추가)
    }, [gridRef]);

    return (
        <div className="flex flex-col gap-3 h-full min-h-0">
            <GridContextWrapper<GridRowData>
            	enableCellCalculation
                gridRef={gridRef}
                paddedGridData={PRD2030_R01List}
                initSelected={initSelectedPRD2030_R01List}
				defaultRowData={defaultProductResultData}
                baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
                defaultColumnState={defaultColumnState}
                setColumnState={setColumnState}
                localStorageKey={COLUMN_STATE_KEY}
                menuItems={undefined}
            >
                <Grid_Select<GridRowData>
                    ref={gridRef}
                    rowData={PRD2030_R01List}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    getRowId={getRowIdForGrid}
                    onCellKeyDown={undefined}
					setHoveredRowIndex={setHoveredRowIndex}
					columnStateKey={COLUMN_STATE_KEY}
					setColumnState={setColumnState}
                    onGridReady={(params) => {
                        params.api.getDisplayedRowCount();
                        params.api.getRenderedNodes();
                    }}
                    shiftRangeSelection={shiftRangeSelectionConfig}
					onRowDoubleClicked={handleRowDoubleClick}
                    pinnedBottomRowData={pinnedBottomRow}
                    rowClassRules={rowClassRules}
                    getRowStyle={getRowStyleForPUR2020}
                    rowDragManaged={true}
                    rowDragEntireRow={true}
                    onRowDragEnd={handleRowDragEnd}
                    isRowSelectable={(node) => !!node.data}
                    suppressRowClickSelection={false}
                    ensureDomOrder={true}
                    scrollTop={scrollTop}
                    setScrollTop={setScrollTop}
                />
            </GridContextWrapper>
        </div>
    );
}


